import { useState } from "react";
import { motion } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { Nav } from "@/components/marketing/Nav";
import { CtaFooter } from "@/components/marketing/CtaFooter";
import { Reveal } from "@/components/Reveal";

const inputCls =
  "w-full bg-white border border-navy/15 px-4 py-3 text-sm text-navy focus:outline-none focus:border-gold transition-colors";
const labelCls = "font-mono text-[10px] tracking-[0.25em] uppercase text-navy/50 block mb-2";

const SERVICES = [
  "Echocardiogram",
  "12-Lead ECG",
  "Rhythm Monitoring",
  "ABPM",
  "One-Stop Clinic",
];

const initial = { name: "", practice: "", role: "", email: "", phone: "", service: SERVICES[0], message: "" };

const Section = ({ no, title, children, testId }) => (
  <Reveal>
    <div className="border-t border-navy/10 pt-10 mt-16" data-testid={testId}>
      <h2 className="font-serif text-2xl lg:text-3xl font-semibold text-navy mb-6">{title}</h2>
      <div className="space-y-5 text-sm lg:text-base text-navy/60 leading-relaxed max-w-3xl">
        {children}
      </div>
    </div>
  </Reveal>
);

export default function PrivateGp() {
  const [form, setForm] = useState(initial);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await api.post("/contact", {
        name: form.name,
        organization: form.practice,
        role: form.role,
        email: form.email,
        phone: form.phone,
        enquiry_type: `Private GP — ${form.service}`,
        message: form.message,
      });
      setDone(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-ivory min-h-screen" data-testid="private-gp-page">
      <Nav />

      <section className="pt-32 pb-16 max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold mb-4">
            Private GP & Independent Healthcare
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4169E1] leading-tight" data-testid="pgp-title">
            Private GP Clinic
          </h1>
          <p className="font-serif italic text-xl lg:text-2xl text-navy/80 mt-4 max-w-3xl">
            Cardiac diagnostics inside your own practice.
          </p>
          <p className="mt-8 text-base text-navy/60 leading-relaxed max-w-2xl">
            If your patients currently leave the practice for an echocardiogram, you lose
            the follow-through — the appointment, the timing of the report, and often the
            patient.
          </p>
          <p className="mt-5 text-base text-navy/60 leading-relaxed max-w-2xl">
            We run dedicated cardiac diagnostic sessions on your premises. We supply the
            ultrasound system and the accredited operator; you provide a suitable clinical
            room and retain the patient record. Sessions can be booked as a regular weekly
            or monthly clinic, or as one-off lists when demand builds up.
          </p>
        </motion.div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-10">
        <Section no="01" title="One-stop cardiac clinic" testId="pgp-onestop">
          <p>
            We can run your session as a one-stop clinic: the patient is seen, scanned and
            reported within the same visit.
          </p>
          <p>
            The study is acquired and reported on site, so the result is available to your
            GP before the patient leaves the building. Where appropriate, the patient can
            have their ECG, echocardiogram and consultation in a single appointment rather
            than three separate journeys.
          </p>
          <p className="font-medium text-navy border-l-2 border-gold pl-5">
            For a private patient, this is often the difference between a diagnostic
            pathway that takes an afternoon and one that takes three weeks.
          </p>
        </Section>

        <Section no="02" title="Rhythm monitoring without the journeys" testId="pgp-rhythm">
          <p>Ambulatory monitoring does not require the patient to attend twice.</p>
          <p>
            We can courier the monitor directly to the patient's home. Our adhesive
            patch-type recorders are straightforward for patients to apply themselves —
            fitting instructions are supplied — and at the end of the recording period the
            patient returns the device in a pre-addressed, prepaid envelope. No fitting
            appointment, no return trip to hand the device back.
          </p>
          <p>
            For the patient this removes two journeys and two appointments. For the
            practice it frees clinical rooms and staff time — and it reduces the travel
            associated with a diagnostic episode, which matters to practices working
            towards their own sustainability commitments.
          </p>
        </Section>

        <Section no="03" title="Typical arrangement" testId="pgp-arrangement">
          <ol className="space-y-4 list-none">
            {[
              "You triage and book your own patients.",
              "We attend with equipment for an agreed session length.",
              "Studies are acquired and reported by an accredited physiologist.",
              "Reports are returned the same day where the session runs as a one-stop clinic, otherwise within an agreed number of working days.",
              "Urgent or unexpected findings are escalated to your named clinician the same day, by an agreed route.",
              "Images and reports are held by the practice as data controller, as agreed in writing.",
            ].map((item, i) => (
              <li key={i} className="flex gap-5">
                <span className="font-mono text-xs tracking-[0.2em] text-gold shrink-0 pt-1">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ol>
        </Section>

        <Section no="04" title="Why practices work with us" testId="pgp-why">
          <ul className="space-y-5 list-none">
            {[
              ["No capital outlay.", "A cardiac ultrasound system is a significant purchase that then needs an accredited operator to be worth anything. We bring both."],
              ["Cost scales with demand.", "Sessions are booked as you need them. An empty list costs you nothing."],
              ["Patients stay with you.", "The investigation, the result and the follow-up consultation all remain within the practice."],
              ["A service you can offer.", "On-site echocardiography, rhythm monitoring and ABPM distinguish a practice from others in the same area, particularly where corporate or membership patients are involved."],
              ["Reporting you can stand behind.", "Studies are reported by a practitioner accredited in echocardiography by the British Society of Echocardiography and the European Association of Cardio-Vascular Imaging."],
            ].map(([head, body]) => (
              <li key={head} className="flex gap-5">
                <span className="text-gold shrink-0 pt-1">✦</span>
                <span>
                  <strong className="text-navy font-semibold">{head}</strong> {body}
                </span>
              </li>
            ))}
          </ul>
        </Section>

        <Section no="05" title="Also available to your practice" testId="pgp-corporate">
          <p>
            Many private GP practices hold corporate accounts and are asked for workplace
            health services they cannot staff. We deliver corporate cardiovascular
            screening on employer premises, and can do so under your practice's branding
            and clinical governance where that suits your client relationship.
          </p>
        </Section>
      </div>

      <section id="enquire" className="bg-navy py-24 mt-16 scroll-mt-24" data-testid="pgp-enquiry-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold mb-4">
                Enquire About a Session
              </p>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-ivory leading-tight mb-6">
                Tell us about your practice.
              </h2>
              <p className="text-sm text-ivory/60 leading-relaxed max-w-md">
                Share a few details and our clinical team will respond within one working
                day to discuss session formats, governance and scheduling.
              </p>
              <p className="mt-6 text-xs text-ivory/40 leading-relaxed max-w-md border-l-2 border-gold/50 pl-4">
                Please do not include any patient-identifiable information in this form.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div className="border border-gold/50 bg-ivory p-10" data-testid="pgp-form-success">
                <h3 className="font-serif text-2xl font-semibold text-navy mb-3">Enquiry received.</h3>
                <p className="text-sm text-navy/60 leading-relaxed">
                  Thank you — the CVI clinical team will review your practice details and
                  respond within one working day.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="pgp-enquiry-form">
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Name</label>
                  <input required value={form.name} onChange={set("name")} data-testid="pgp-name-input" className={inputCls} placeholder="Dr Jane Smith" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Practice</label>
                  <input required value={form.practice} onChange={set("practice")} data-testid="pgp-practice-input" className={inputCls} placeholder="Kingsbridge Medical Practice" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Role</label>
                  <input value={form.role} onChange={set("role")} data-testid="pgp-role-input" className={inputCls} placeholder="GP Partner / Practice Manager" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Email</label>
                  <input type="email" required value={form.email} onChange={set("email")} data-testid="pgp-email-input" className={inputCls} placeholder="you@practice.co.uk" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Telephone</label>
                  <input type="tel" value={form.phone} onChange={set("phone")} data-testid="pgp-phone-input" className={inputCls} placeholder="07…" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Service of Interest</label>
                  <select value={form.service} onChange={set("service")} data-testid="pgp-service-select" className={inputCls}>
                    {SERVICES.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={`${labelCls} !text-ivory/50`}>Message</label>
                  <textarea rows={5} required value={form.message} onChange={set("message")} data-testid="pgp-message-input" className={inputCls} placeholder="Patient volumes, preferred session days, current referral route…" />
                </div>

                {error && (
                  <p data-testid="pgp-form-error" className="sm:col-span-2 text-sm text-crimson border border-crimson/30 bg-crimson/5 px-4 py-3">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="pgp-submit-button"
                  className="sm:col-span-2 w-full bg-gold text-navy font-mono text-xs tracking-[0.25em] uppercase py-4 hover:bg-champagne transition-colors disabled:opacity-50"
                >
                  {sending ? "Sending…" : "Enquire About a Session"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <CtaFooter />
    </div>
  );
}
